import React, { useEffect, useRef } from "react";
import { getFormattedNames, getParsedSelectedTalents, getSpecOrClassIcon, getSpritesheetUrl } from "../../../lib/wowHelpers";
import Icon from "../../Common/Icon";
import TalentTreeHeader from "./TalentTreeHeader";
import { MediaQuery } from "@outplayed/responsive";
import OctagonTalentIcon from "../../Common/OctagonTalentIcon";
import "react-tooltip/dist/react-tooltip.css";
import WowTooltip from "components/Tooltips/WowTooltip";

const StandardTreeHeader = ({ treeType, classType, specType, isClassTree }) => {
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const treeLabel = treeType == "spec" ? displaySpecName : displayClassName;

  return (
    <div className="content-section-header">
      <Icon iconSize="20px" classType={"circle"} staticDataImg={getSpecOrClassIcon(isClassTree, dataClassName, dataSpecName)} />
      <div className="tree-label">{treeLabel} Talents</div>
    </div>
  );
};

const WOWTalentTreeSection = (props) => {
  const {
    classAndSpecTree,
    classType,
    specType,
    selectedTalents,
    className,
    hideHeader,
    treeHeaderComponent = StandardTreeHeader,
  } = props;

  return (
    <div className={`wow-talent-tree-container ${className ?? ""}`}>
      {!hideHeader && <TalentTreeHeader {...props} />}
      <div className="wow-talent-tree-section">
        <WOWTalentTreeContainer
          tree={classAndSpecTree?.classNodes ?? {}}
          isClassTree={true}
          specType={specType}
          classType={classType}
          treeType="class"
          selectedTalentsData={selectedTalents}
          treeHeaderComponent={treeHeaderComponent}
        />
        <div className="divider" />
        <WOWTalentTreeContainer
          tree={classAndSpecTree?.specNodes ?? {}}
          specType={specType}
          classType={classType}
          treeType="spec"
          selectedTalentsData={selectedTalents}
          treeHeaderComponent={treeHeaderComponent}
        />
      </div>
    </div>
  );
};

const drawLine = (ctx, canvasRef, x1, y1, x2, y2) => {
  // TODO: rework this function. The x values correlate with the y axis and y axis corrolates to x.
  // This is because the grid function works from top left to bottom right in increasing order.
  // Implementing a temp fix via adding values to the startX and startY vars below to get close to visual level.
  let path = new Path2D();
  let c = canvasRef.current;

  x1 = x1 + 0.45;
  x2 = x2 + 0.85;
  // y2= y2+0.1;

  // single_width and single_height are constants from the css file wow-build
  const single_width = 26;
  const single_height = 55;
  // the icons are width and height of 36 px
  const startX = single_width * x1 * 2;
  const startY = (single_height * y1 - single_height / 2 - 10) * 2;
  const endX = (single_width * x2 - single_width + 16) * 2;
  const endY = (single_height * y2 - single_height / 2 - 10) * 2;

  c.width = c.clientWidth;
  c.height = c.clientHeight;

  path.moveTo(startX, startY);
  path.lineTo(endX, endY);

  return path;
};

const WOWTalentTreeContainer = (props) => {
  const { tree, treeType, classType, specType, selectedTalentsData, isClassTree, treeHeaderComponent: Header } = props;

  const { selectedTalents, selectedTalentNodeIds, selectedTalentsValues } = getParsedSelectedTalents(selectedTalentsData);

  const canvasRef = useRef(null);
  useEffect(() => {
    var ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 3;
    ctx.beginPath();
    const paths = Object.entries(tree)
      .map(([key, node]) => {
        const prevNodes = node.previousNodeIds.map((nodeId) => {
          return tree[nodeId];
        });

        return prevNodes.map((prevNode) => {
          const selectedTalentsArray = selectedTalents;
          let lineColor = "#454582";
          let lineIsYellow = "#FFAF33";

          if (node.spells.length > 0 && prevNode.spells.length > 0) {
            if (selectedTalentsArray.includes(prevNode.id.toString()) && selectedTalentsArray.includes(node.id.toString())) {
              lineColor = lineIsYellow;
            }
          }
          return [drawLine(ctx, canvasRef, prevNode.column + 1, prevNode.row + 1, node.column + 1, node.row + 1), lineColor];
        });
      })
      .flat();

    ctx.canvas.width = 1000;
    ctx.canvas.height = 1100;
    ctx.lineWidth = 4;
    for (let i = 0; i < paths.length; i++) {
      const path = paths[i];
      ctx.strokeStyle = path[1];
      ctx.stroke(path[0]);
    }
    ctx.closePath();
  }, [tree, selectedTalentsData]);

  const spriteSheet = getSpritesheetUrl(classType, specType, treeType);
  return (
    <div className="tree-container">
      <Header treeType={treeType} classType={classType} specType={specType} isClassTree={isClassTree} />
      <div className="talent-tree">
        {Object.entries(tree).map(([key, node], index) => {
          const classType = node.type;
          if (node.spells.length > 0) {
            if (node.spells.length > 1) {
              // Normal selectable spell node.
              for (let i = 0; i < node.spells.length; i++) {
                // Use nodeEntryId for data from WCL and the definition ID for data from the Blizzard API.
                if (
                  selectedTalentNodeIds.includes(node.spells[i].nodeEntryId.toString()) ||
                  selectedTalentNodeIds.includes(node.spells[i].definitionId.toString())
                ) {
                  return (
                    <GridIcon
                      key={index + node.spells[i].nodeEntryId.toString()}
                      gridRow={(node.row + 1).toString()}
                      gridColumn={(node.column + 1).toString()}
                      classType={classType}
                      type={"spell"}
                      color={selectedTalents.includes(node.id.toString()) ? "#FF9B00" : null}
                      talentTreeGreyout={!selectedTalents.includes(node.id.toString())}
                      talentSelectedNumber={selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]}
                      talentMaxNumber={node.spells[i].maxRanks.toString()}
                      talentName={node.spells[i].name}
                      talentDescription={
                        node.spells[i].rankDescriptions[
                          parseInt(selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]) - 1
                        ]
                      }
                      id={node.spells[i].spellId}
                      spriteSheet={spriteSheet}
                      sprite={node.spells[i].sprite}
                    />
                  );
                }
              }
            }
            return (
              <GridIcon
                key={index + node.spells[0].nodeEntryId.toString()}
                gridRow={(node.row + 1).toString()}
                gridColumn={(node.column + 1).toString()}
                classType={classType}
                type={"spell"}
                color={selectedTalents.includes(node.id.toString()) ? "#FF9B00" : null}
                talentTreeGreyout={!selectedTalents.includes(node.id.toString())}
                talentSelectedNumber={selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]}
                talentMaxNumber={node.spells[0].maxRanks.toString()}
                talentName={node.spells[0].name}
                talentDescription={
                  selectedTalents.includes(node.id.toString())
                    ? node.spells[0].rankDescriptions[
                        parseInt(selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]) - 1
                      ]
                    : node.spells[0].description
                }
                talentDef={node.spells[0].definitionId.toString()}
                id={node.spells[0].spellId}
                spriteSheet={spriteSheet}
                sprite={node.spells[0].sprite}
              />
            );
          }
          return (
            <GridIcon
              key={index}
              gridRow={(node.row + 1).toString()}
              gridColumn={(node.column + 1).toString()}
              classType={classType}
            />
          );
        })}
        <canvas className="talent-tree-canvas" ref={canvasRef} width="1000" height="1100" />
      </div>
    </div>
  );
};

const GridIcon = (props) => {
  const {
    gridRow,
    gridColumn,
    classType,
    color,
    type,
    id,
    talentTreeGreyout,
    talentSelectedNumber,
    talentMaxNumber,
    talentName,
    talentDescription,
    talentDef,
    spriteSheet,
    sprite,
  } = props;

  const getIcon = (classType) => {
    // Some icons are not correct from wow api. Instead, use hardcoded images that we manually update
    let staticDataImg = `https://static.bigbrain.gg/wow/spell/assets/${id}.webp`;
    const hardcodedIds = [
      392256, // Restoration Druid: Harmonious Blooming
      33763, // Restoration Druid: Lifebloom
      196737, // Brewmaster Monk: High Tolerance
      191837, // Mistweaver Monk: Essence Font
      31850, // Protection Paladin: Ardent Defender
      393114, // Protection Paladin: Improved Ardent Defender
      267344, // Retribution Paladin: Art of War
      108209, // Subtelty Rogue: Shadow Focus
      264119, // Demonology Warlock: Summon Vilefiend
      215571, // Fury Warrior: Frothing Berserker
      392792, // Arms Warrior: Frothing Berserker
      392790, // Protection Warrior: Frothing Berserker
      384042, // Protection Warrior: Unnerving Focus
      207200, // Blood Death Knight: Permafrost
      221536, // Blood Death Knight: Heartbreaker
    ];
    if (hardcodedIds.includes(id)) {
      staticDataImg = `https://static.bigbrain.gg/wow/spell/assets/hardcoded/${id}.webp`;
    } else if (spriteSheet && sprite) {
      staticDataImg = spriteSheet;
    }

    const spriteData = sprite;
    if (classType == 3) {
      return (
        <>
          <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM">
            <OctagonTalentIcon
              iconSize="25px"
              color={color}
              staticDataImg={staticDataImg}
              talentSelectedNumber={talentSelectedNumber}
              talentMaxNumber={talentMaxNumber}
              isInnerBorderPresent={true}
              spriteData={spriteData}
            />
          </MediaQuery>
          <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
            <OctagonTalentIcon
              iconSize="36px"
              color={color}
              staticDataImg={staticDataImg}
              talentSelectedNumber={talentSelectedNumber}
              talentMaxNumber={talentMaxNumber}
              isInnerBorderPresent={true}
              spriteData={spriteData}
            />
          </MediaQuery>
        </>
      );
    }
    return (
      <>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM">
          <Icon
            iconSize="25px"
            classType={classType == 2 ? "circle wow-talent-icon" : "wow-talent-icon"}
            color={color}
            talentTreeGreyout={talentTreeGreyout}
            talentSelectedNumber={talentSelectedNumber}
            talentMaxNumber={talentMaxNumber}
            isInnerBorderPresent={true}
            staticDataImg={staticDataImg}
            spriteData={spriteData}
            style={{
              marginTop: "-15px",
              marginLeft: "-15px",
            }}
          />
        </MediaQuery>
        <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
          <Icon
            iconSize="36px"
            classType={classType == 2 ? "circle wow-talent-icon" : "wow-talent-icon"}
            color={color}
            talentTreeGreyout={talentTreeGreyout}
            talentSelectedNumber={talentSelectedNumber}
            talentMaxNumber={talentMaxNumber}
            isInnerBorderPresent={true}
            staticDataImg={staticDataImg}
            spriteData={spriteData}
            style={{
              marginTop: "-10px",
              marginLeft: "-10px",
            }}
          />
        </MediaQuery>
      </>
    );
  };

  return (
    <div style={{ gridRow: gridRow, gridColumn: gridColumn, zIndex: "100" }}>
      <WowTooltip type="spell" id={id} name={talentName} def={talentDef} rank={talentSelectedNumber}>
        {getIcon(classType)}
      </WowTooltip>
    </div>
  );
};

export default React.memo(WOWTalentTreeSection);
